import { RHFAutoCompleteVirtualize } from '@openx/components-core/lib/AutoComplete/RHFAutoCompleteVirtualize';
import { FormSection } from '@openx/components-core/lib/FormSection/FormSection';
import { RIGHT_COLUMN_WIDTH } from '@openx/constants/form';
import { useCallback, useMemo } from 'react';
import { type Path, useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { v4 as uuid } from 'uuid';
import { useProviders } from '../../hooks/useProviders';
import { type ExportTypeValue, exportTargetsByType, exportType, MatchingOperator } from '../constants';
import { AudienceFields, type AudienceForm } from '../types';
import { getExportTypeLabels } from '../utils/getExportTypeLabels';

type AudienceOptionsProps = {
  isEdit?: boolean;
};

export const AudienceOptions = ({ isEdit }: AudienceOptionsProps) => {
  const { t } = useTranslation();
  const {
    setValue,
    formState: { isSubmitting },
    control,
  } = useFormContext<AudienceForm>();

  const observableAccountId = useWatch({ name: AudienceFields.OrganizationId, control });

  const { hasProvidersWithBidstream, hasProvidersWithBidstreamPlus, loadingProviders } =
    useProviders(observableAccountId);

  const handleExportTypeChange = useCallback(
    (value: ExportTypeValue) => {
      setValue(AudienceFields.ExportType, value, { shouldValidate: true });
      setValue(AudienceFields.ExportTargets, exportTargetsByType[value]);
      setValue(AudienceFields.DirectAudienceProvider, null);
      setValue(AudienceFields.NormalizedDefinition, {
        excludes: {
          groups: [{ uuid: uuid(), operator: MatchingOperator.INTERSECTION }],
          operator: MatchingOperator.INTERSECTION,
        },
        includes: {
          groups: [{ uuid: uuid(), operator: MatchingOperator.INTERSECTION }],
          operator: MatchingOperator.INTERSECTION,
        },
      });
    },
    [setValue]
  );

  const exportTypeLabels = useMemo(() => getExportTypeLabels(t), [t]);

  const exportOptions = useMemo(() => {
    const options: Array<ExportTypeValue> = [exportType.OA_MATCH];

    if (hasProvidersWithBidstream) {
      options.push(exportType.BIDSTREAM);
    }

    if (hasProvidersWithBidstreamPlus) {
      options.push(exportType.BIDSTREAM_PLUS);
    }

    return options;
  }, [hasProvidersWithBidstream, hasProvidersWithBidstreamPlus]);

  // Keep Match Type mounted while providers load to avoid a race after org change.
  // Hide only after load when Identity Graph is the sole option.
  if (!loadingProviders && exportOptions.length === 1) {
    return;
  }

  return (
    <FormSection divider title={t('Audience Options')} rightColumnSize={RIGHT_COLUMN_WIDTH}>
      <RHFAutoCompleteVirtualize
        name={AudienceFields.ExportType satisfies Path<AudienceForm>}
        options={exportOptions}
        getOptionLabel={option => exportTypeLabels[option]}
        textFieldProps={{
          placeholder: t('[ Select Match Type ]'),
          label: t('Match Type'),
          margin: 'dense',
        }}
        onChange={(_, value) => {
          handleExportTypeChange(value);
        }}
        disableClearable
        textFieldReadOnly
        data-test="match-type"
        disabled={isSubmitting || isEdit || loadingProviders || exportOptions.length === 1}
      />
    </FormSection>
  );
};
